import React from 'react';
import SlideLayout, { SlideTitle, SlideContent } from '../components/SlideLayout';
import { Cpu, TrendingUp, Zap, CheckCircle } from 'lucide-react';

const R1ZeroSlide = () => {
  return (
    <SlideLayout maxWidth="6xl">
      <SlideTitle 
        subtitle="Proving reasoning emerges through reinforcement learning alone"
      >
        DeepSeek-R1-Zero: Proof of Concept
      </SlideTitle>
      
      <SlideContent>
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left: Base Model & Method */}
          <div>
            <div className="slide-card mb-4 border-primary/30">
              <h3 className="font-heading font-semibold text-foreground mb-3 flex items-center gap-2">
                <Cpu className="w-5 h-5 text-primary" />
                Base Model
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between items-center p-2 rounded bg-muted/50">
                  <span className="text-sm text-muted-foreground">Model</span>
                  <span className="font-mono text-primary">DeepSeek-V3-Base</span>
                </div>
                <div className="flex justify-between items-center p-2 rounded bg-muted/50">
                  <span className="text-sm text-muted-foreground">Total Parameters</span>
                  <span className="font-mono text-foreground">671B</span>
                </div>
                <div className="flex justify-between items-center p-2 rounded bg-muted/50">
                  <span className="text-sm text-muted-foreground">Active per Token</span>
                  <span className="font-mono text-accent">37B</span>
                </div>
              </div>
            </div>
            
            <div className="slide-card border-secondary/30">
              <h3 className="font-heading font-semibold text-foreground mb-3 flex items-center gap-2">
                <Zap className="w-5 h-5 text-secondary" />
                Training Method
              </h3>
              <ul className="text-sm text-muted-foreground space-y-2">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                  <span><strong className="text-foreground">Pure RL</strong> - No supervised fine-tuning</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                  <span><strong className="text-foreground">Rule-based rewards</strong> - No neural reward models</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                  <span><strong className="text-foreground">10,400 steps</strong> across 1.6 epochs</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                  <span>Output length: <span className="font-mono">32K → 65K</span> tokens</span>
                </li>
              </ul>
            </div>
          </div>
          
          {/* Right: Results */}
          <div className="slide-card h-fit">
            <h3 className="font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-accent" />
              AIME 2024 Results
            </h3>
            
            <div className="space-y-4">
              <div className="p-4 rounded-xl bg-muted/30">
                <div className="flex items-end justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Before RL Training</span>
                  <span className="text-3xl font-mono text-muted-foreground">15.6%</span>
                </div>
                <div className="w-full h-2 rounded-full bg-muted">
                  <div className="h-full rounded-full bg-muted-foreground/50" style={{ width: '15.6%' }} />
                </div>
              </div>
              
              <div className="p-4 rounded-xl bg-accent/10 border border-accent/30">
                <div className="flex items-end justify-between mb-2">
                  <span className="text-sm text-accent">After RL (pass@1)</span>
                  <span className="text-3xl font-mono text-accent">71.0%</span>
                </div>
                <div className="w-full h-2 rounded-full bg-muted">
                  <div className="h-full rounded-full bg-accent" style={{ width: '71%' }} />
                </div>
              </div>
              
              <div className="p-4 rounded-xl bg-primary/10 border border-primary/30">
                <div className="flex items-end justify-between mb-2">
                  <span className="text-sm text-primary">Majority Voting (64 samples)</span>
                  <span className="text-3xl font-mono text-primary">86.7%</span>
                </div>
                <div className="w-full h-2 rounded-full bg-muted">
                  <div className="h-full rounded-full bg-primary" style={{ width: '86.7%' }} />
                </div>
                <p className="text-xs text-primary mt-2">Matches OpenAI-o1-0912</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="mt-6 p-4 rounded-xl bg-primary/10 border border-primary/20">
          <p className="text-sm text-muted-foreground">
            <strong className="text-primary">Key Finding:</strong> Sophisticated reasoning behaviors emerged without explicit training—validating that human-defined patterns may limit model exploration.
          </p>
        </div>
      </SlideContent>
    </SlideLayout>
  );
};

export default R1ZeroSlide;
